import type { PortableTextBlock } from '@portabletext/types';

import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import type { SanityPredictedLineupTeamSection } from '@lib/sanity';

export type ContentVideo =
  components['schemas']['VideosResponse']['data'][number];
// The OpenAPI schema types Portable Text as an open object; the renderer and
// the block hydrator both want the real block type.
export type PremiumBodyBlock = PortableTextBlock;

/**
 * Which editorial document to read. A team reveal is addressed by its
 * contributor's handle as well as its slug, matching the URL it renders under.
 */
export type ContentDocumentSelector =
  | { type: 'articlePage'; slug: string }
  | { type: 'teamReveal'; authorSlug: string; slug: string };

const FORBIDDEN = 403;
const NOT_FOUND = 404;

/**
 * Fetches premium (members-only) post content from the ffh-backend by slug.
 * Requires a valid paid entitlement, which the backend enforces after reading
 * the offloaded content. Returns undefined when the member is not entitled
 * (403) or the post has no offloaded content (404), mirroring scoreline-app
 * so the page simply keeps showing the preview.
 */
export const fetchPost = async (
  slug: string,
  accessToken: string
): Promise<string | undefined> => {
  const { data, response } = await ffhClient().GET('/content/posts/{slug}', {
    params: { path: { slug } },
    headers: authHeader(accessToken)
  });

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(`Failed to fetch post: ${response.status}`);
  }

  const { rendered } = data.data;
  if (rendered == null) return undefined;
  return typeof rendered === 'string' ? rendered : JSON.stringify(rendered);
};

/**
 * Fetches the premium body of a Sanity article or team reveal as Portable
 * Text. The API enforces the PRO or ULTRA entitlement. Returns undefined when
 * the member is not entitled (403) or the document has no premium body (404),
 * so the page keeps showing the teaser and paywall rather than blanking.
 */
export const fetchPremiumBody = async (
  selector: ContentDocumentSelector,
  accessToken: string
): Promise<PremiumBodyBlock[] | undefined> => {
  const { data, response } =
    selector.type === 'articlePage'
      ? await ffhClient().GET('/content/articles/{slug}/premium', {
          params: { path: { slug: selector.slug } },
          headers: authHeader(accessToken)
        })
      : await ffhClient().GET('/content/team-reveals/{author}/{slug}/premium', {
          params: {
            path: { author: selector.authorSlug, slug: selector.slug }
          },
          headers: authHeader(accessToken)
        });

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(`Failed to fetch premium body: ${response.status}`);
  }

  return data.data.body as unknown as PremiumBodyBlock[];
};

/**
 * Fetches the YouTube video list surfaced by the ffh-backend. Public endpoint
 * (no auth), cached upstream.
 */
export const fetchVideos = async (): Promise<ContentVideo[]> => {
  const { data, response } = await ffhClient().GET('/content/videos');

  if (!data) {
    throw new Error(`Failed to fetch videos: ${response.status}`);
  }

  return data.data;
};

/**
 * Fetches the gated club sections of the predicted lineups page (FFH-2919).
 * Same contract as the premium body: the API enforces PRO or ULTRA, and a 403
 * or 404 comes back as undefined so the page keeps the pitches and shows the
 * paywall in place of the words.
 */
export const fetchPremiumTeamSections = async (
  accessToken: string
): Promise<SanityPredictedLineupTeamSection[] | undefined> => {
  const { data, response } = await ffhClient().GET(
    '/content/predicted-lineups/premium',
    { headers: authHeader(accessToken) }
  );

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(
      `Failed to fetch premium club sections: ${response.status}`
    );
  }

  return data.data.teams as unknown as SanityPredictedLineupTeamSection[];
};
